import React from "react";
import { useLocation, Link } from "react-router-dom";
import { Icon, Thumbnail, Tooltip } from "@culturehq/components";
import * as CHQ from "chq";

import { useProfile } from "app/context";
import { areEventsEnabled, arePostsEnabled, t, Trans } from "app/utils";

import UsersModal from "components/common/UsersModal";
import PostModal from "components/PostModal";
import UserThumbnail from "components/person/UserThumbnail";
import tagsTypes from "constants/tagsTypes";
import getUserName from "app/utils/getUserName";
import SharePhotoAction from "./suggested-actions/SharePhotoAction";
import TopInterests from "./TopInterests";

const MAX_USERS_TO_SHOW = 6;
const MAX_NAMES_TO_SHOW = 2;

const ProfileThumbnail: React.FC = () => {
  const { profile } = useProfile();

  return (
    <div className="FeedItem__user">
      <Link to="/profile">
        <Thumbnail
          className="Thumbnail"
          image={profile.avatar.mediumUrl}
          title={getUserName(profile)}
        />
      </Link>
    </div>
  );
};

const getUsersUnion = (index: number, usersNumber: number) => {
  if (index < MAX_NAMES_TO_SHOW - 1) {
    if (usersNumber === MAX_NAMES_TO_SHOW) {
      return " and ";
    }
    if (usersNumber === 1) {
      return "";
    }

    return ", ";
  }
  return "";
};

type CreateEventActionProps = {
  isWithLabel?: boolean;
};

const CreateEventAction: React.FC<CreateEventActionProps> = ({ isWithLabel = false }) => (
  <Link to="/events/new/type" className="Button Button-inverted Button-primary" aria-label={t("Create an event")}>
    <Icon icon="calendar-star" />{isWithLabel && <Trans>Create Event</Trans>}
  </Link>
);

type ExploreSuggestedActionsProps = {
  selectedOptions: Array<any>;
  filteredUsers: any;
  onGetUsers: (page: number) => void;
};

const ExploreSuggestedActions: React.FC<ExploreSuggestedActionsProps> = ({
  children, selectedOptions, filteredUsers: { users, pagination }, onGetUsers
}) => {
  const location = useLocation();
  const { profile: rawProfile } = useProfile();

  // This shouldn't be necessary but it is because currently useProfile is lying
  // about its state. In reality it's not a union, it's just a Profile, because
  // if we've gotten this far we're definitely logged in.
  const profile = rawProfile as CHQ.Profile;
  const { userCreatedPostsEnabled } = profile.organization;

  const getMentions = () => (
    selectedOptions.map((option: any) => (
      { type: option.category, entityId: option.value, name: option.label }
    ))
  );


  const getEmptyDescription = () => {
    const BreakException = {};
    try {
      selectedOptions.forEach(option => {
        if (option.category !== tagsTypes.ORG_VALUES) {
          throw BreakException;
        }
      });
      return "Values are tagged to content, but not to people. Explore related content below or create your own.";
    } catch (_) {
      return "No people match these tags yet, but that's okay. You can get things started!";
    }
  };

  return (
    <section aria-label={t("Explore section")}>
      <div className="FeedItem FeedItem__SuggestedActions FeedItem__Explore">
        <h2 className="title-hidden"><Trans>Explore section</Trans></h2>
        <div className="FeedItem__Search">
          <ProfileThumbnail />
          {children}
        </div>
        {selectedOptions.length === 0 && (
          <div className="FeedItem__Explore-Suggested">
            <span className="FeedItem__Explore-Suggested-title"><Trans>Suggested:</Trans></span>
            {" "}
            <TopInterests />
          </div>
        )}
        {selectedOptions.length > 0 && (
          <>
            {users.length > 0 ? (
              <div className="FeedItem__Explore-People__Container">
                <UsersModal
                  users={users}
                  pagination={pagination}
                  loading={false}
                  fetchUsers={onGetUsers}
                  mentions={getMentions()}
                  title={`Who has ${selectedOptions.length > 1 ? "these" : "this"} tag${selectedOptions.length > 1 ? "s" : ""}?`}
                >
                  {(onTrigger: any) => (
                    <div className="FeedItem__Explore-People" onClick={onTrigger}>
                      {users.map((user: CHQ.User, index: number) => (
                        index < MAX_USERS_TO_SHOW && <UserThumbnail key={user.id} user={user} />
                      ))}
                      {pagination.totalCount > MAX_USERS_TO_SHOW && <span className="FeedItem__Explore-People__more Thumbnail">+{pagination.totalCount - MAX_USERS_TO_SHOW}</span>}
                      <span className="FeedItem__Explore-People__description" role="button">
                        {users.map((user: CHQ.User, index: number) => (
                          <span key={user.id}>
                            {index < MAX_NAMES_TO_SHOW && getUserName(user)}
                            {getUsersUnion(index, users.length)}
                          </span>
                        ))}
                        {pagination.totalCount > MAX_NAMES_TO_SHOW && (
                          <span>
                            <Trans>
                              {" "}and {pagination.totalCount - MAX_NAMES_TO_SHOW} others
                            </Trans>
                          </span>
                        )}
                        {" "}
                        {users.length > 1 ? "have" : "has"} {selectedOptions.length > 1 ? "these tags" : "this tag"}
                      </span>
                    </div>
                  )}
                </UsersModal>
                {!arePostsEnabled(profile) && areEventsEnabled(profile) && (
                  <div className="EventButton">
                    <CreateEventAction isWithLabel />
                  </div>
                )}
              </div>
            ) : (
              <div className="FeedItem__Explore-People__Container">
                <Icon className="Empty-Icon" icon="ios-people" />
                <span className="Empty-Description"><Trans>{getEmptyDescription()}</Trans></span>
              </div>
            )}
            {arePostsEnabled(profile) && (
              <div className="FeedItem__preview">
                <div className="main-container">
                  {(profile.organizationAdmin || userCreatedPostsEnabled) && (
                    <PostModal initialOpen={location.hash === "#post"} placeholder={t("Post an update to these tags")} prefilledOptions={selectedOptions} />
                  )}
                </div>
                <div className="buttons-container">
                  <div className="FeedItem__SuggestedActions__preview">
                    {areEventsEnabled(profile) && (
                      <Tooltip tip={t("Create an event")}>
                        <CreateEventAction />
                      </Tooltip>
                    )}
                    {(profile.organizationAdmin || userCreatedPostsEnabled) && (
                      <Tooltip tip={t("Share a photo")}><SharePhotoAction prefilledOptions={selectedOptions} />{" "}</Tooltip>
                    )}
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
};

export default ExploreSuggestedActions;
